/**
 * The garden: a placed plot plus everything growing in it.
 *
 * Owns tap routing, sowing, the growth clock, and serialisation. The renderer
 * and the UI both talk to this and to nothing below it.
 *
 * Since v2 the garden advances on wall-clock time rather than on taps, so
 * `update()` is where maturity actually happens — not `handleTap`. A tap now
 * only *tends*. This is the whole reason the app is worth reopening.
 */

import * as THREE from 'three';
import { buildPlot, type Plot } from '../models/plot';
import { buildPavilion, type Pavilion } from '../models/pavilion';
import { Plant, STAGES, type PlantState } from './Plant';
import { SPECIES, type Species, type SpeciesId } from '../content/species';
import { harvestYield } from './economy';
import { TIERS } from '../content/tiers';
import { capacityFor } from './tiers';

/**
 * The pavilion's radius as a multiple of the plot's.
 *
 * Fixed ratio rather than a fixed distance: the architecture has to stay a
 * ring *around* the garden at every tier, and at 1.6 m a wall set 0.16 m out
 * would be standing in the wetland.
 */
const PAVILION_RATIO = 1.23;
import { buildVisitorFigures, type VisitorFigures } from '../models/visitors';
import { CROWD, GATE_ANGLES } from '../content/visitors';
import { Crowd, type GardenView, type PlantView } from './visitors';
import type { AudioEngine } from '../core/AudioEngine';
import type { ParticleSystem } from '../core/Particles';

export interface GardenEvents {
  /** A plant crossed a growth stage, whether by tending or by the clock. */
  onGrow?(plant: Plant, byTending: boolean): void;
  /** A plant just reached full maturity — this is what counts as a tree. */
  onMature?(plant: Plant, treeCount: number): void;
  /**
   * A tap tended a growing plant, whether or not it crossed a stage.
   * `onGrow` fires only on a stage change, so it cannot count taps.
   */
  onTend?(plant: Plant): void;
  /** A new seed went into the ground. */
  onSow?(plant: Plant): void;
  /** Player tapped the plot but every slot is taken. */
  onFull?(): void;
  /** Player tapped a plant that was already fully grown. */
  onAlreadyMature?(plant: Plant): void;
  /**
   * A mature plant has been armed for harvest by a first tap. Nothing has
   * happened yet — the second tap is what lifts it.
   */
  onHarvestOffer?(plant: Plant, yieldSeedlings: number): void;
  /** A mature plant was lifted. Its bed is free again. */
  onHarvest?(plant: Plant, yieldSeedlings: number): void;
  /** The armed plant was let go, by timing out or by tapping elsewhere. */
  onHarvestDisarm?(): void;
}

/**
 * How long a mature plant stays armed after the first tap.
 *
 * Harvest is the only destructive act in the game, and it happens on the same
 * gesture as tending — so it takes two taps on the *same* specimen. Long-press
 * was the obvious alternative and is the wrong one here: taps already resolve
 * on `pointerup` against a 600 ms threshold so that dragging to orbit does not
 * sow, and stacking a second time-based gesture on top of that would make both
 * unreliable.
 */
const HARVEST_ARM_MS = 6000;

/** The tap-driven format shipped in v1. Read for migration only. */
interface GardenSaveV1 {
  version: 1;
  plants: { speciesId: string; stage: number; slot: number; seed: number }[];
}

export interface GardenSave {
  version: 2;
  plants: PlantState[];
}

export type AnyGardenSave = GardenSave | GardenSaveV1;

/** What changed in the player's absence, for the welcome-back summary. */
export interface OfflineReport {
  awayMs: number;
  maturedWhileAway: number;
  grewWhileAway: number;
  /** The specimens that came in while nobody was looking. */
  matured: Plant[];
}

export class Garden {
  readonly root = new THREE.Group();

  private plot: Plot;
  /* The architecture belongs to the plot, not to the scene: it has to move,
     rotate and hide with it. Owning it here also means `dispose()` stays
     honest without `App` having to remember it exists. */
  private pavilion: Pavilion;
  private plants: Plant[] = [];
  private raycaster = new THREE.Raycaster();
  private placed = false;
  private seedCounter = 1;

  /** The mature plant one tap away from being lifted, and when it was armed. */
  private armed: Plant | null = null;
  private armedAt = 0;

  /*
   * Passers-by. The crowd is pure logic (`game/visitors.ts`) and the figures
   * are one instanced rig (`models/visitors.ts`); the garden owns both
   * because it owns the ground they walk on and has to rebuild them when a
   * district opens, exactly as it does the plot and the pavilion.
   *
   * The figures hang off `root`, never off `plot.root`, which is what
   * `handleTap` raycasts. A visitor therefore cannot intercept a tap meant
   * for soil no matter where it is standing.
   */
  private crowd = new Crowd();
  private figures: VisitorFigures;
  private visitorsOn = true;

  private scene: THREE.Scene;
  private audio: AudioEngine;
  private particles: ParticleSystem;
  private events: GardenEvents;

  constructor(
    scene: THREE.Scene,
    audio: AudioEngine,
    particles: ParticleSystem,
    events: GardenEvents = {},
  ) {
    this.scene = scene;
    this.audio = audio;
    this.particles = particles;
    this.events = events;
    this.plot = buildPlot(0);
    this.root.add(this.plot.root);
    this.pavilion = buildPavilion(this.plot.radius * PAVILION_RATIO);
    this.root.add(this.pavilion.root);
    this.figures = buildVisitorFigures(CROWD.maxConcurrent, this.figureScale());
    this.root.add(this.figures.root);
    this.root.visible = false;
    this.scene.add(this.root);
  }

  get isPlaced(): boolean {
    return this.placed;
  }

  /** Plants that have reached maturity. This is the number the UI shows. */
  get treeCount(): number {
    return this.plants.filter((p) => p.isMature).length;
  }

  get plantCount(): number {
    return this.plants.length;
  }

  get capacity(): number {
    return this.plot.slots.length;
  }

  get isFull(): boolean {
    return this.plants.length >= this.plot.slots.length;
  }

  get freeSlots(): number {
    return Math.max(0, this.plot.slots.length - this.plants.length);
  }

  get tier(): number {
    return this.plot.tier;
  }

  /** Outer radius of the terrace, metres. `preview` frames the shot with it. */
  get radius(): number {
    return this.plot.radius;
  }

  /**
   * Open the garden out to a new district.
   *
   * The plot and the architecture are rebuilt; **the plants are not touched**.
   * Bed positions are absolute metres fixed at the moment their ring opens
   * (see `models/plot.ts`), so every specimen already standing keeps exactly
   * the position it had — the ground simply extends past it. Anything else
   * would make growing the garden rearrange the garden.
   */
  setTier(tier: number): boolean {
    let next = THREE.MathUtils.clamp(Math.floor(tier), 0, TIERS.length - 1);

    /*
     * A district never closes. `tierFor` is derived from a lifetime counter
     * that only ratchets up, so this cannot happen in play — but a shrink
     * would strand every plant standing in an outer bed in mid-air, and that
     * is too quiet a failure to leave to an invariant somewhere else.
     */
    const occupied = this.plants.reduce((max, p) => Math.max(max, p.slot), -1);
    while (next < this.plot.tier && capacityFor(next) <= occupied) next += 1;

    if (next === this.plot.tier) return false;

    this.root.remove(this.plot.root);
    this.plot.dispose();
    this.root.remove(this.pavilion.root);
    this.pavilion.dispose();

    this.plot = buildPlot(next);
    this.root.add(this.plot.root);
    this.pavilion = buildPavilion(this.plot.radius * PAVILION_RATIO);
    this.root.add(this.pavilion.root);

    /*
     * The ground the crowd walks on just changed shape, and the figures are
     * sized against the plot the way the pavilion is. Rebuild both, and send
     * everybody home rather than leaving a visitor mid-stroll along a ring
     * that no longer exists.
     */
    this.root.remove(this.figures.root);
    this.figures.dispose();
    this.crowd.clear();
    this.figures = buildVisitorFigures(CROWD.maxConcurrent, this.figureScale());
    this.root.add(this.figures.root);

    if (this.placed) {
      this.plot.reveal();
      this.pavilion.reveal();
    }
    return true;
  }

  /** Read-only view for scoring and the register. */
  get all(): readonly Plant[] {
    return this.plants;
  }

  /** Height of the tallest thing growing, in metres. Used to frame the shot. */
  get contentHeight(): number {
    let tallest = 0;
    for (const p of this.plants) {
      tallest = Math.max(tallest, p.currentHeight);
    }
    return tallest;
  }

  /** Distinct species the player has grown to maturity at least once. */
  get collectedSpecies(): Set<SpeciesId> {
    const set = new Set<SpeciesId>();
    for (const p of this.plants) {
      if (p.isMature) set.add(p.species.id as SpeciesId);
    }
    return set;
  }

  /**
   * The still-growing plant closest to maturity. The HUD counts this one down,
   * because "next tree in 4m" is the line that makes leaving feel like
   * anticipation rather than abandonment.
   */
  nextReady(nowMs: number): Plant | null {
    let best: Plant | null = null;
    for (const p of this.plants) {
      if (p.isMature) continue;
      if (!best || p.remainingMs(nowMs) < best.remainingMs(nowMs)) best = p;
    }
    return best;
  }

  place(position: THREE.Vector3, quaternion: THREE.Quaternion): void {
    this.root.position.copy(position);
    // Keep the plot flat and only inherit the surface's yaw. Hit-test normals
    // on textured floors wobble, and a garden that sits visibly tilted reads
    // as a bug even when the tracking is technically correct.
    const e = new THREE.Euler().setFromQuaternion(quaternion, 'YXZ');
    this.root.rotation.set(0, e.y, 0);
    this.root.visible = true;
    this.placed = true;
    this.plot.reveal();
    this.pavilion.reveal();
    this.audio.thud();
  }

  /** Pick the plot back up so the player can reposition it. */
  unplace(): void {
    this.placed = false;
    this.root.visible = false;
    this.crowd.clear();
    this.figures.sync([]);
  }

  /* ---------------------------------------------------------------- *
   * Input
   * ---------------------------------------------------------------- */

  /**
   * Route a screen tap.
   *
   * A tap on an existing plant tends it. A tap anywhere else on the plot sows
   * `species` into the nearest free slot. Returns true if the tap was
   * consumed, so the caller knows not to treat it as a UI miss.
   */
  handleTap(ndc: THREE.Vector2, camera: THREE.Camera, species: Species): boolean {
    if (!this.placed) return false;

    this.raycaster.setFromCamera(ndc, camera);

    // Plants first — they sit on top of the soil and should win ties.
    const pickers = this.plants.map((p) => p.pickTarget);
    const plantHits = this.raycaster.intersectObjects(pickers, false);
    if (plantHits.length > 0) {
      this.tendPlant(plantHits[0].object.userData.plant as Plant);
      return true;
    }

    // Any tap that is not on the armed plant lets it go. Leaving a specimen
    // armed while the player does something else is how an accident happens.
    this.disarm();

    // Otherwise, did they hit the plot itself?
    const plotHits = this.raycaster.intersectObject(this.plot.root, true);
    if (plotHits.length > 0) {
      if (this.isFull) {
        this.events.onFull?.();
        return true;
      }
      this.sow(species, plotHits[0].point);
      return true;
    }

    return false;
  }

  /* ---------------------------------------------------------------- *
   * Harvest
   * ---------------------------------------------------------------- */

  /** The specimen currently one tap from being lifted, if any. */
  get armedPlant(): Plant | null {
    return this.armed;
  }

  private disarm(): void {
    if (!this.armed) return;
    this.armed = null;
    this.events.onHarvestDisarm?.();
  }

  /**
   * Lift a mature specimen. The bed is free again and the plant is gone.
   *
   * Everything a harvested plant contributed — its species, its weather mark,
   * its points — has to survive somewhere outside the garden, because none of
   * it is stored on the plant: a mutation is derived from the timeline and the
   * score is computed from what is standing. `App` folds the return value into
   * the profile before this returns to the render loop.
   */
  private harvest(plant: Plant): void {
    const paid = harvestYield(plant.species.rarity, plant.mutation);

    this.audio.bloom(plant.species.rootSemitone);
    this.particles.burst(plant.crownWorldPosition, plant.species.bloom, 40, 1.2);

    const i = this.plants.indexOf(plant);
    if (i >= 0) this.plants.splice(i, 1);
    this.root.remove(plant.root);

    this.armed = null;
    this.events.onHarvest?.(plant, paid);

    // After the event: the handler reads species, rarity and mutation off the
    // plant to bank them, and disposing first would leave it reading a
    // half-released model.
    plant.dispose();
  }

  private tendPlant(plant: Plant): void {
    const now = Date.now();
    const before = plant.stage;

    if (!plant.tend(now)) {
      /*
       * Fully grown, so there is nothing to tend — this tap is about harvest.
       * The first one arms, the second one lifts. Tapping a *different* mature
       * plant re-arms rather than harvesting, which is what stops a stray tap
       * across a crowded plot taking the wrong tree.
       */
      if (this.armed === plant && now - this.armedAt < HARVEST_ARM_MS) {
        this.harvest(plant);
        return;
      }

      this.armed = plant;
      this.armedAt = now;
      this.audio.pluck(plant.species.rootSemitone, STAGES, 0.35);
      this.particles.burst(plant.crownWorldPosition, plant.species.bloom, 8, 0.5);
      this.events.onHarvestOffer?.(plant, harvestYield(plant.species.rarity, plant.mutation));
      this.events.onAlreadyMature?.(plant);
      return;
    }

    this.events.onTend?.(plant);

    // The note climbs with the stage — the rising melody from the original.
    this.audio.pluck(plant.species.rootSemitone, plant.stage);
    this.particles.burst(
      plant.crownWorldPosition,
      plant.species.leaf,
      10 + plant.stage * 3,
      0.6 + plant.stage * 0.12,
    );

    if (plant.stage > before) this.events.onGrow?.(plant, true);
    if (plant.isMature) this.bloom(plant);
  }

  /**
   * Sow a species without the player aiming at a bed — how a seed pack plants
   * what it drew. Fills outward from the middle of the plot so a pack of three
   * does not land in a row against the rim.
   */
  sowSpecies(species: Species): Plant | null {
    if (!this.placed) return null;
    return this.sow(species, null);
  }

  /**
   * Sow into whichever free slot is closest to `nearWorldPoint`, or to the
   * centre of the plot when nobody aimed.
   */
  private sow(species: Species, nearWorldPoint: THREE.Vector3 | null): Plant | null {
    const taken = new Set(this.plants.map((p) => p.slot));
    const local = nearWorldPoint
      ? this.root.worldToLocal(nearWorldPoint.clone())
      : new THREE.Vector3();

    let bestSlot = -1;
    let bestDist = Infinity;
    this.plot.slots.forEach((slot, i) => {
      if (taken.has(i)) return;
      const d = slot.distanceToSquared(local);
      if (d < bestDist) {
        bestDist = d;
        bestSlot = i;
      }
    });

    if (bestSlot < 0) {
      this.events.onFull?.();
      return null;
    }

    const plant = new Plant(species, bestSlot, this.seedCounter++, Date.now());
    plant.root.position.copy(this.plot.slots[bestSlot]);
    // Random yaw so a row of the same species doesn't look stamped.
    plant.root.rotation.y = Math.random() * Math.PI * 2;
    this.root.add(plant.root);
    this.plants.push(plant);

    this.audio.pluck(species.rootSemitone, 0, 0.7);
    this.particles.burst(
      this.root.localToWorld(this.plot.slots[bestSlot].clone()),
      species.leaf,
      14,
      0.55,
    );
    this.events.onSow?.(plant);
    return plant;
  }

  private bloom(plant: Plant): void {
    this.audio.bloom(plant.species.rootSemitone);
    this.particles.burst(plant.crownWorldPosition, plant.species.bloom, 44, 1.35);
    this.events.onMature?.(plant, this.treeCount);
  }

  /* ---------------------------------------------------------------- *
   * Loop
   * ---------------------------------------------------------------- */

  /** Lantern brightness, 0 in daylight to 1 when the sky has gone dark. */
  setNight(amount: number): void {
    this.pavilion.setNight(amount);
  }

  /**
   * Freeze or thaw the crowd.
   *
   * Frame rate outranks the feature, and a visitor is the first thing that
   * should stop costing anything: the app switches this off while the page is
   * hidden and anywhere the session is not actually being played.
   */
  setVisitorsEnabled(on: boolean): void {
    if (this.visitorsOn === on) return;
    this.visitorsOn = on;
    if (!on) {
      this.crowd.clear();
      this.figures.sync([]);
    }
  }

  /**
   * Figure scale, against the terrace the kinds were authored for.
   *
   * Same call `models/pavilion.ts` makes and for the same reason: the plot
   * more than doubles in radius as districts open, and a visitor in
   * absolute metres would go from a figure in a garden to an ant in a field.
   */
  private figureScale(): number {
    return this.plot.radius / TIERS[0].radius;
  }

  /** The flat description `game/visitors.ts` walks over. Built per frame. */
  private visitorView(): GardenView {
    const plot = this.plot;
    // Ring radii come off the slots rather than the tier table, so they are
    // whatever the plot actually built — one source of truth, and it is the
    // one the beds are in.
    const rings: number[] = [];
    for (const slot of plot.slots) {
      const r = Math.hypot(slot.x, slot.z);
      if (!rings.some((existing) => Math.abs(existing - r) < 0.01)) rings.push(r);
    }

    const now = Date.now();
    const plants: PlantView[] = this.plants.map((p) => {
      const state = p.toState();
      const age = Math.max(0, now - p.sownAt);
      const grow = p.species.growSeconds * 1000;
      return {
        slot: p.slot,
        x: p.root.position.x,
        z: p.root.position.z,
        speciesId: p.species.id,
        progress: p.progressAt(now),
        mature: p.isMature,
        rare: p.species.rarity === 'rare' || p.species.rarity === 'legendary',
        mutated: p.mutation !== null,
        /*
         * An observation, never a penalty: a seed that has been in the ground
         * longer than it needed and has never once been tended. Nothing about
         * the plant changes because of it — growth stays a pure function of
         * the clock (AGENTS.md) and being away must never cost anything.
         */
        neglect:
          p.isMature || state.tends > 0
            ? 0
            : Math.min(1, Math.max(0, age / grow - 0.5) / 0.5),
      };
    });

    return {
      plot: {
        radius: plot.radius,
        pavilionRadius: plot.radius * PAVILION_RATIO,
        gateAngles: GATE_ANGLES,
        slots: plot.slots.map((s) => ({ x: s.x, z: s.z })),
        ringRadii: rings,
        groundYAt: (r) => plot.groundYAt(r),
      },
      plants,
      capacity: plot.slots.length,
    };
  }

  update(dt: number, time: number, camera?: THREE.Camera): void {
    this.plot.update(dt, time);
    this.pavilion.update(dt, time);

    const now = Date.now();

    // An armed plant that is never confirmed goes back to being just a tree.
    if (this.armed && now - this.armedAt >= HARVEST_ARM_MS) this.disarm();

    for (const p of this.plants) {
      // A stage crossed by the clock alone still sings. A garden that plays
      // its own melody while you watch is most of what makes the waiting
      // pleasant rather than dead.
      const crossed = p.syncToClock(now);
      if (crossed > 0) {
        this.audio.pluck(p.species.rootSemitone, p.stage, 0.5);
        this.particles.burst(
          p.crownWorldPosition,
          p.species.leaf,
          8 + p.stage * 2,
          0.5 + p.stage * 0.1,
        );
        this.events.onGrow?.(p, false);
        if (p.isMature) this.bloom(p);
      }
      p.update(dt, time);
    }

    // Last, so the crowd reads the garden as it stands this frame — a plant
    // that just matured is mature to the visitor looking at it.
    if (this.visitorsOn && this.placed) {
      this.crowd.update(dt, this.visitorView());
      this.figures.sync(this.crowd.all);
      if (camera) this.figures.faceCamera(camera);
    }
  }

  /**
   * Fast-forward every plant to the current clock in silence, reporting what
   * changed rather than performing it.
   *
   * Needed in two places, and it was the second that made it obvious this
   * belonged in one method: restoring a save, and *returning to a
   * backgrounded tab*. `requestAnimationFrame` stops in a hidden tab, so the
   * garden is frozen while the clock is not — and without this, switching
   * back after ten minutes fired every missed stage of every bed at once. A
   * dozen plants each ringing four notes simultaneously is a car alarm.
   */
  catchUp(sinceMs: number): OfflineReport {
    const now = Date.now();
    const matured: Plant[] = [];
    let grew = 0;

    for (const p of this.plants) {
      const was = p.stageAt(sinceMs);
      p.syncToClock(now, true);
      if (p.stage > was) {
        grew += p.stage - was;
        if (p.isMature && was < STAGES) matured.push(p);
      }
    }

    return {
      awayMs: Math.max(0, now - sinceMs),
      maturedWhileAway: matured.length,
      grewWhileAway: grew,
      matured,
    };
  }

  /** Remove everything planted, keeping the plot where it is. */
  clear(): void {
    this.armed = null;
    for (const p of this.plants) {
      this.root.remove(p.root);
      p.dispose();
    }
    this.plants = [];
  }

  /* ---------------------------------------------------------------- *
   * Persistence
   * ---------------------------------------------------------------- */

  serialize(): GardenSave {
    return { version: 2, plants: this.plants.map((p) => p.toState()) };
  }

  /**
   * Rebuild a saved garden. The plot must already be placed.
   *
   * `lastSeenAt` is when the save was written; everything between then and
   * now is the offline progress the player gets to be told about. Nothing is
   * simulated — growth is a function of the clock, so restoring *is* catching
   * up.
   */
  restore(save: AnyGardenSave, lastSeenAt: number): OfflineReport {
    this.clear();
    const states = migrate(save, lastSeenAt);
    let maxSeed = 0;

    for (const state of states) {
      const species = SPECIES[state.speciesId as SpeciesId];
      if (!species) continue; // species retired since the save was written
      if (state.slot < 0 || state.slot >= this.plot.slots.length) continue;

      const plant = new Plant(
        species,
        state.slot,
        state.seed,
        state.sownAt,
        state.tendMs,
        state.tends,
      );
      plant.root.position.copy(this.plot.slots[state.slot]);
      plant.root.rotation.y = (state.seed * 2.399963) % (Math.PI * 2);
      // Start each plant where it stood when the save was written, so
      // `catchUp` has a truthful baseline to diff against.
      plant.syncToClock(lastSeenAt, true);

      this.root.add(plant.root);
      this.plants.push(plant);
      maxSeed = Math.max(maxSeed, state.seed);
    }

    this.seedCounter = maxSeed + 1;
    return this.catchUp(lastSeenAt);
  }

  dispose(): void {
    this.clear();
    this.figures.dispose();
    this.pavilion.dispose();
    this.plot.dispose();
    this.scene.remove(this.root);
  }
}

/**
 * Bring any save format up to the current `PlantState`.
 *
 * A v1 save recorded only a tap count, so there is no true sowing time to
 * recover. We back-date each plant to whatever `sownAt` would produce the
 * stage it had reached, which preserves exactly what the player earned and
 * lets it carry on growing from there.
 */
function migrate(save: AnyGardenSave, lastSeenAt: number): PlantState[] {
  if (save.version === 2) return save.plants;

  return save.plants.map((p) => {
    const species = SPECIES[p.speciesId as SpeciesId];
    const growMs = (species?.growSeconds ?? 60) * 1000;
    const fraction = Math.min(1, Math.max(0, p.stage / STAGES));
    return {
      speciesId: p.speciesId,
      slot: p.slot,
      seed: p.seed,
      sownAt: lastSeenAt - growMs * fraction,
      tendMs: 0,
      tends: 0,
    };
  });
}
